import React, { useState, useEffect } from "react";
import {
	Image,
	message,
	Row,
	Form,
	Typography,
	Alert,
	Space,
	Table,
	Spin,
	Col,
	Select,
} from "antd";
import Button from '@mui/material/Button';
import { LoadingOutlined } from '@ant-design/icons';
import LinkPlaid from "../../components/buttons/linkplaid";
import LogoArpariWhite from "../../style/frontpage/img/arpari-logos/Logo-Arpari-navy.png";
import { backendGetRequest, backendPatchWithoutRefresh, backendPostWithoutRefresh, backendPostWithRedirect } from "../../helpers/callBackend";
import GenericModal from "../../components/buttons/genericModal"
import { toPrice } from "../../helpers/stringFormat";
import { documentTitle } from "../../components/header/documentTitle";
import CircularProgress from '@mui/material/CircularProgress';

const { Text } = Typography;

function AchDebit() {
	const [companyName, setCompanyName] = useState("");
	const [messageToPayee, setMessageToPayee] = useState("");
	const [paymentAmount, setPaymentAmount] = useState(0)
	const [paymentMemo, setPaymentMemo] = useState("")

	const [processorToken, setProcessorToken] = useState("");
	const [accessToken, setAccessToken] = useState("");
	const [linkedAccounts, setLinkedAccounts] = useState([]);
	const [visible, setVisible] = useState(false);
	const [accountName, setAccountName] = useState("");
	const [loadingLinkedAccounts, setLoadingLinkedAccounts] = useState(false);
	const [loadingProcessorToken, setLoadingProcessorToken] = useState(false);
	const [errorMessage, setErrorMessage] = useState("")

	const [isPaid, setIsPaid] = useState(false);
	const [isLiveLink, setIsLiveLink] = useState(false);
	const [loading, setLoading] = useState(true);
	const [idempotencyKey, setIdempotencyKey] = useState("")

	useEffect(() => {
		let pathname = window.location.pathname
		if (pathname.slice(-1) === '/') {
			pathname = pathname.substring(0, pathname.length - 1);
		}
		const urlParts = pathname.split('/');
		const idempotencyKey = urlParts.slice(-1)[0];
		setIdempotencyKey(idempotencyKey)

		backendGetRequest(
			"/api/payments/ach-debit-payee/",
			{ params: { idempotency_key: idempotencyKey } },
		).then((response) => {
			if (response?.status === 200) {
				setCompanyName(response?.data?.ach_debit_request?.landlord_info?.name);
				setMessageToPayee(response?.data?.ach_debit_request?.payee_info?.message_to_payee);
				setPaymentAmount(response?.data?.ach_debit_request?.amount);
				setPaymentMemo(response?.data?.ach_debit_request?.payee_info?.memo);
				setIsPaid(response?.data?.ach_debit_request?.is_paid);
				setIsLiveLink(response?.data?.ach_debit_request?.is_live_link);
				setLoading(false);
			} else {
				// setErrorMessage(response.data.description);
				setLoading(false);
			}
		});
	}, []);

	useEffect(() => {
		documentTitle({ title: "Debit" })
	}, [])

	const linkPlaidOnSuccess = (plaidResponse) => {
		setAccessToken(plaidResponse?.data?.access_token);
		setLinkedAccounts(plaidResponse?.data?.linked_accounts);
		setLoadingLinkedAccounts(false)
	}


	const fetchProcessorTokenForSelectedPlaidAccount = (plaidAccountId) => {
		const linkedAccount = linkedAccounts.filter((acc) => acc.id === plaidAccountId)[0];
		setAccountName(linkedAccount.name);
		backendPostWithoutRefresh(
			"/api/plaid-arpari/processor-token/",
			{
				'accountId': plaidAccountId,
				'accessToken': accessToken,
			}
		).then((response) => {
			if (response.data.processor_token && response.data.processor_token.length > 0) {
				setProcessorToken(response.data.processor_token);
				setLoadingProcessorToken(false);
			}
		}).catch((error) => {
			console.error(error);
			setLoadingProcessorToken(false)
			setErrorMessage(error.response.description)
		})
	};

	const postAchDebitPayee = (plaidProcessorToken, accepted, onSuccessFunc) => {
		backendPostWithoutRefresh(
			"/api/payments/ach-debit-payee/",
			{
				idempotency_key: idempotencyKey,
				plaid_processor_token: plaidProcessorToken,
				accepted: accepted
			},
		).then((response) => {
			setVisible(false);
			onSuccessFunc();
		}).catch((error) => {
			setVisible(false);
			console.error(error);
			setErrorMessage(error.response)
		});
	}

	const submitAuthorizedPayment = () => {
		postAchDebitPayee(
			processorToken,
			true,
			() => {
				setIsPaid(true);
				setIsLiveLink(false)
			}
		)
	}

	const declinePayment = () => {
		postAchDebitPayee(
			processorToken,
			false,
			() => {
				setIsLiveLink(false)
			}
		)
	}

	return (

		<div
			style={{
				position: "absolute",
				height: "100%",
				width: "100%",
				display: "grid",
				placeItems: "center",
			}}
		>

			<Space direction="vertical" align="center">
				<Row justify="center">
					<Image width={200} preview={false} src={LogoArpariWhite} />
				</Row>

				{loading ? (
					<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', width: '100%' }}>
						<CircularProgress />
					</div>
				) : isPaid ? (
					<p>
						The ACH debit has been successfully	authorized.
					</p>
				) : isLiveLink ? (
					<>
						{(errorMessage !== "") &&
							<Row justify="center">
								<Alert
									message={errorMessage}
									type="warning"
									closable
								/>
							</Row>
						}

						<Row justify="center">
							{companyName} is requesting authorization for an ACH debit. Please connect your account for payment.
						</Row>

						<Row justify="center">
							{messageToPayee}
						</Row>

						<Row justify="center">
							<Col span={12}>
								<Table
									pagination={false}
									columns={[
										{ title: "", width: "25%", dataIndex: "col1" },
										{ title: "", width: "50%", dataIndex: "col2" },
										{ title: "", width: "25%", dataIndex: "col3" },
									]}
									dataSource={[
										{
											col1: "Amount",
											col2: toPrice(paymentAmount),
										},
										{
											col1: "Memo",
											col2: paymentMemo,
										},
										{
											col1: "Account",
											col2: <>
												{(linkedAccounts === undefined || (linkedAccounts.length < 1 && !loadingLinkedAccounts)) ? (
													<Text type="secondary" italic>Please connect an account via Plaid to proceed with payment.</Text>
												) : (loadingLinkedAccounts) ? (
													<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', width: '100%' }}>
														<CircularProgress />
													</div>
												) : (
													<Select
														placeholder="Please select an account"
														options={linkedAccounts.map(acc => ({ value: acc.id, label: acc.name }))}
														dropdownMatchSelectWidth={false}
														onChange={fetchProcessorTokenForSelectedPlaidAccount}
														style={{ width: '100%' }}
													/>
												)}

											</>,
											col3: <>
												<div
													style={{ marginRight: "10px", width: "150px" }}
												>
													<LinkPlaid
														accessTokenOnSuccess={(plaidResponse) => (linkPlaidOnSuccess(plaidResponse))}
														awaitingOnSuccess={() => (setLoadingLinkedAccounts(true))}
														buttonType="primary"
														buttonText="Connect Account"
														linkCustomizationName="single"
														storePlaidItems={0}
													/>
												</div>
											</>,
										},
									]}
									tableLayout="fixed"

								/>

							</Col>
						</Row>

						<Row justify="center">
							<Space direction="horizontal" style={{ width: '100%' }}>

								<Button
									// loading={submitLoading}
									disabled={processorToken === ""}
									style={{ width: "150px" }}
									onClick={() => setVisible(true)}
									sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
									disableElevation size="small" type="submit" variant="contained" color="primary"
								>
									Submit
								</Button>

								<Button
									style={{ width: "150px" }}
									onClick={() => declinePayment()}
									sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
									disableElevation size="small" type="submit" variant="outlined" color="secondary"
								>
									Decline Request
								</Button>
							</Space>
						</Row>
					</>
				) : (
					<p>
						The ACH debit has been declined or is not valid.
					</p>
				)}


			</Space>

			<GenericModal
				size='md'
				visible={visible}
				handleCancelModal={() => setVisible(false)}
				showModal={() => setVisible(true)}
				modalTitle={'Confirm Payment Authorization'}
				modalBody={
					<Space direction="vertical">
						<Row>
							<Text style={{ fontSize: "16px" }}>
								By clicking the "Authorize" button below, I authorize {companyName}{" "}
								to debit {toPrice(paymentAmount)} from my bank account, {accountName}, on {new Date().toDateString() + ''}.
							</Text>
							<Text italic style={{ fontSize: "12px" }} disabled>
								Transactions made after 3:00 PM ET or on a weekend or holiday will be processed the next
								business day. It is recommended that you printn a copy of this authorization and maintain
								it for your records.
							</Text>
						</Row>

						<Row justify="center">
							<Space direction="horizontal" style={{ width: '100%' }}>

								<Button
									style={{ width: "150px" }}
									onClick={() => submitAuthorizedPayment()}
									sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
									disableElevation size="small" type="submit" variant="contained" color="primary"
								>
									Authorize
								</Button>

								<Button
									style={{ width: "150px" }}
									onClick={() => setVisible(false)}
									sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
									disableElevation size="small" type="submit" variant="outlined" color="secondary"
								>
									Cancel
								</Button>
							</Space>
						</Row>
					</Space>
				}
			/>
		</div>

	);
}

export default AchDebit